import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  CircularProgress,
  TablePagination
} from '@mui/material';
import { format } from 'date-fns';
import { fetchWashData } from '../api/sensorApi';

/**
 * Component that displays a list of tunnel wash events
 */
const WashList = ({ tunnel, maxHeight = 400 }) => {
  const [washData, setWashData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Define colors for different wash types
  const washTypeColors = {
    'Helvask': 'success',
    'Halvvask': 'info',
    'Teknisk vask': 'warning'
  };

  useEffect(() => {
    const loadWashData = async () => {
      setLoading(true);
      try {
        const data = await fetchWashData(tunnel);
        
        // Sort by date, most recent first
        const sortedData = [...data].sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        setWashData(sortedData);
      } catch (error) {
        console.error('Error loading wash data:', error);
        setWashData([]);
      } finally {
        setLoading(false);
      }
    };

    loadWashData();
  }, [tunnel]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Display loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Display message if no data
  if (washData.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          No wash data available for this tunnel.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader aria-label="wash history table">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Tunnel</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {washData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((wash, index) => (
                <TableRow hover key={`${wash.date}-${index}`}>
                  <TableCell>{formatDate(wash.date)}</TableCell>
                  <TableCell>{wash.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={wash.type}
                      color={washTypeColors[wash.type] || 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{wash.tunnel}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={washData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default WashList;